// Paying honestly (compliance round 1, G3 part A): the words every point of
// payment shares. Mirrored by the app's
// lib/features/payments/domain/paying_honestly.dart: change both together.
// Pure: no Nuxt imports, so the unit tests import it directly.

// CCRs 2013 reg 14(3): the final button on a paid acceptance must say the
// member is obliged to pay. Owner decision 30 Sep 2026: the label carries the
// price and never suggests paying NOW, because the charge comes later (after
// the session, or once an admin places them). `amount` is always the
// formatted price ("£5.00"); these functions never format money themselves.
// Replaces "Book and pay" / "Accept and pay" / "Join and pay".

/** Booking a paid meet date: "Book, pay £5.00" (says pay, never "now"; owner, 30 Sep 2026). */
export const bookLabel = (amount) => (amount ? `Book, pay ${amount}` : 'Book, pay');

/** Accepting a paid offer (waitlist, admin add, price, terms or date change): "Accept, pay £5.00". */
export const acceptPaidLabel = (amount) => (amount ? `Accept, pay ${amount}` : 'Accept, pay');

// DMCC 2024 s.226/230: the server charges a no-show the full fee, and with
// check-in off everyone still booked is billed as attended. Never "only if
// you attend".
export const NO_SHOW_LINE = "If you don't cancel and don't come, you pay in full.";
export const CHARGED_AFTER_LINE = `Charged after the session. ${NO_SHOW_LINE}`;
// A waitlist place is never charged: a spot that opens is an offer the member
// must accept (MeetRegisterRules.PromoteWaitlistHead).
export const WAITLIST_CHARGE_LINE = 'Nothing is charged unless you accept a spot.';

// PSRs 2017 reg 67: what saving a card authorises, in our words, replacing
// Stripe's own mandate line (which names the platform).
export const SAVE_CARD_AUTHORISATION =
    'Saving your card lets the clubs you book or enter with charge it for what you owe them.';

/** A paid offer is accepted with "Accept, £5.00" (the offered price); a free one keeps its own words. */
export const offerAcceptLabel = (paid, freeLabel, amount) => (paid ? acceptPaidLabel(amount) : freeLabel);

/** A paid acceptance with club terms stays disabled until the terms are ticked. */
export const paidAcceptanceBlocked = ({paid, termsHtml, ticked}) => !!paid && !!termsHtml && !ticked;

/**
 * The server's refusal of a paid acceptance for want of the meet's current
 * terms ("Please accept this meet's terms and conditions before attending").
 * The date page opens the terms instead of showing it raw.
 */
export const isTermsRefusal = (error) =>
    (error?.global ?? []).some(m => /accept this meet's terms/i.test(String(m)));

/**
 * CCRs 2013 reg 13: the club is the seller and is named with its address
 * ("You're paying Leicester Squash, 1 High Street, Leicester, LE1 1AA.").
 * The address is left out when the club has none; null without a club name.
 */
export const payeeLine = (clubName, clubAddress) => {
    if (!clubName) return null;
    const address = (clubAddress ?? '').trim();
    return address ? `You're paying ${clubName}, ${address}.` : `You're paying ${clubName}.`;
};

/**
 * Stripe Payment Element options. Link is deliberately hidden: cards are saved
 * on the Tap Leagues platform customer for one-click pay, so Stripe's Link
 * wallet (and its in-form signup prompt) only competes with our own saved-card
 * flow. Where a card is being SAVED, Stripe's own mandate line (which names the
 * platform) is replaced by ours, SAVE_CARD_AUTHORISATION.
 */
export const paymentElementOptions = ({savingCard = false} = {}) => ({
    wallets: {link: 'never'},
    ...(savingCard ? {terms: {card: 'never'}} : {}),
});

// ── Bill to: the payer accepts first (G3 part B, C5-1) ──
// An admin's Bill to is a REQUEST. Only an Accepted one moves a player's
// charges to the payer; until then the player pays their own way (or the club
// settles offline). Statuses travel by name from the API.
export const BillToStatus = {Pending: 'Pending', Accepted: 'Accepted', Declined: 'Declined'};

/** The payer's final button names who they are paying for (CCRs reg 14): "Accept, pay for Jamie". */
export const billToAcceptLabel = (firstName) => {
    const name = (firstName ?? '').trim();
    return name ? `Accept, pay for ${name}` : 'Accept, pay';
};

/** Accepting needs the terms ticked and a saved card: that card is what gets charged. */
export const billToAcceptBlocked = ({ticked, hasCard}) => !ticked || !hasCard;

/** What the admin is told in the Bill to dialog. */
export const billToAdminNote = (name) =>
    `We'll ask ${(name ?? '').trim() || 'them'} to agree first. Nothing is charged to them until they accept.`;

/** The roster button: only an agreed request reads as "Billed to". */
export const billToRowLabel = (assignment) => {
    if (!assignment) return 'Bill to…';
    const name = assignment.billToName;
    if (assignment.status === BillToStatus.Accepted) return `Billed to ${name}`;
    if (assignment.status === BillToStatus.Declined) return `${name} declined`;
    return `Asked ${name}`;
};

/** One line on where a request stands, for the admin dialog. */
export const billToStatusLine = (assignment) => {
    if (!assignment) return null;
    const name = assignment.billToName;
    if (assignment.status === BillToStatus.Accepted) return `${name} agreed to pay.`;
    if (assignment.status === BillToStatus.Declined) return `${name} declined.`;
    return `Waiting for ${name} to agree.`;
};

/** A request can be sent again until it is agreed. */
export const canResendBillTo = (assignment) => !!assignment && assignment.status !== BillToStatus.Accepted;

// ── Entry fees: joining commits to the fee (owner decision 28 Sep 2026, replaces D109;
//    the button reads "Join, £12.00 fee" since 30 Sep 2026) ──
// The button a member presses to enter a paid competition commits them to the
// fee (CCRs 2013 reg 14), and the tick above it is their express request for
// the entry to start (reg 36). Mirrored by the app's paying_honestly.dart.
// `amount` is always the formatted fee ("£12.00"); these functions never
// format money themselves.
//
// An API that predates this sends no entryFeeTermsHtml / entryFeeTermsVersion:
// then nothing here asks for terms, the button keeps its old words, nothing
// extra is sent, and the Pay step asks for the tick exactly as before.

/** The join button when joining straight away: "Join, £12.00 fee". */
export const joinWithFeeLabel = (amount) => `Join, pay ${amount}`;

/**
 * A held join (the competition waits for an admin to place new players): the fee
 * is owed once placed, not at the button (R2 batch B). The API says which
 * (joinHeldForPlacement, its HoldsJoins rule). Mirrored by the app's heldJoinLabel.
 */
export const heldJoinLabel = (amount) => `Join (${amount} when placed)`;

/** Same words as the API's PendingPlacementLinks.FeeOnPlacementLine and the app's heldJoinFeeLine. */
export const heldJoinFeeLine = (amount) => `You'll be asked to pay ${amount} once an admin places you.`;

/** The join link or waiting row says the join waits for an admin to place it. */
export const entryFeeJoinHeld = (source) => source?.joinHeldForPlacement === true;

/** The join button when an admin approves first: nothing is owed until then. */
export const requestToJoinPaidLabel = (amount) => `Request to join (${amount} if accepted)`;

/**
 * The line under the fee on the join card, saying when it is paid. `entity` is
 * the page's own word for the competition ("ladder"). Same words as the app's
 * entryFeeJoinNote.
 */
export const entryFeeJoinNote = ({requiresApproval, entity, held = false, amount = ''}) => requiresApproval
    ? 'You only pay this fee if an admin accepts your request.'
    : held
        ? heldJoinFeeLine(amount)
        : `Joining means you agree to pay this fee. You can pay straight after joining, or later from the ${entity} page.`;

/** The tick above either button. */
export const entryFeeJoinTickLabel = (amount, held = false) =>
    `I accept the entry fee terms, agree to pay ${amount}${held ? ' once I\'m placed' : ''}, and want my entry to start when the competition starts.`;

/** The tick at the Pay step, for someone who has not accepted the current terms. */
export const ENTRY_FEE_PAY_TICK =
    'I accept the entry fee terms and want my entry to start when the competition starts.';
/** The same at the no-login pay link, where the payer is often a parent. */
export const ENTRY_FEE_PAY_LINK_TICK =
    'I accept the entry fee terms and want the entry to start when the competition starts.';

/** Shown at the Pay step instead of the tick when this member already accepted these terms. */
export const ENTRY_FEE_TERMS_ALREADY_ACCEPTED = "You've already accepted these entry fee terms.";

/** The API codes a join or a request can be refused with. */
export const ENTRY_FEE_TERMS_CHANGED = 'entryFeeTermsChanged';
export const ENTRY_FEE_TERMS_REQUIRED = 'entryFeeTermsRequired';

/**
 * What a join link (or a competition's detail, for a public request) says about
 * its entry fee terms. `asksTerms` is true only when a fee applies AND the API
 * sent both the terms and their version: anything less is an older API, which
 * keeps today's behaviour.
 */
export const entryFeeJoinTerms = (source) => {
    const paid = source?.entryFeeMinor != null;
    const html = source?.entryFeeTermsHtml || null;
    const version = source?.entryFeeTermsVersion ?? null;
    return {
        paid,
        termsHtml: paid ? html : null,
        termsVersion: paid ? version : null,
        asksTerms: paid && !!html && version != null,
    };
};

/**
 * The invite page's main button. An existing request only takes a message, so
 * it keeps its words; otherwise the words say what pressing it commits to.
 */
export const inviteJoinButtonLabel = ({requestSent, requiresApproval, asksTerms, amount, held = false}) => {
    if (requestSent) return 'Add message to request';
    if (requiresApproval) return asksTerms ? requestToJoinPaidLabel(amount) : 'Request to join';
    if (!asksTerms) return 'Accept invitation';
    return held ? heldJoinLabel(amount) : joinWithFeeLabel(amount);
};

/** A paid join or request stays disabled until the terms are ticked. */
export const entryFeeJoinBlocked = ({asksTerms, ticked}) => !!asksTerms && !ticked;

/**
 * The fields added to the join call or the access application. Nothing is added
 * when the terms are not asked, which the API reads as an older client.
 */
export const entryFeeJoinFields = ({asksTerms, ticked, termsVersion}) =>
    asksTerms ? {acceptEntryFeeTerms: !!ticked, entryFeeTermsVersion: termsVersion} : {};

/** The API refused because the terms moved on while the page was open. */
export const isEntryFeeTermsChanged = (error) => error?.code === ENTRY_FEE_TERMS_CHANGED;

/** The Pay step asks for the tick unless this member already accepted these terms. */
export const entryFeePayNeedsTick = (obligation) => !obligation?.termsAcceptedByMe;

/** A payment stays disabled until the tick, when the tick is asked for. */
export const entryFeePayBlocked = (obligation, ticked) => entryFeePayNeedsTick(obligation) && !ticked;

/**
 * The body of a checkout or saved-card payment. `acceptTerms` is sent only when
 * the tick was asked for: the server accepts an earlier acceptance by this
 * member at the current version on its own.
 */
export const entryFeePayBody = (obligation, extra = {}) =>
    entryFeePayNeedsTick(obligation) ? {...extra, acceptTerms: true} : {...extra};

// ── Assigned payer (R2 batch B item 3) ──
// An admin can ask a member to pay another team's entry fee. The payer's banner
// and payments row say who it is for, not "to enter". Mirrored by the app's
// entryFeeAssignedToMe / entryFeeOwedTitle / entryFeeOwedLine; the sentence is
// the API's OwedItems.AssignedFeeReason.

/** The fee was assigned to this member (they are asked to pay it for a team). */
export const entryFeeAssignedToMe = (obligation, userId) =>
    !!obligation?.assignedUserId && !!userId && obligation.assignedUserId === userId;

/** The payments row's title: "Pay for {team}" for an assigned fee, else the competition. */
export const entryFeeOwedTitle = (obligation, userId) =>
    entryFeeAssignedToMe(obligation, userId) ? `Pay for ${obligation.teamName || 'a team'}` : obligation?.entityName;

/** Why an assigned fee is theirs to pay. */
export const entryFeeAssignedLine = (obligation) =>
    `${obligation?.clubName || 'Your club'} asked you to pay ${obligation?.teamName || 'a team'}'s entry fee for ${obligation?.entityName || 'this competition'}.`;

/** Admin: what assigning a payer does (GetMine shows an assigned fee only to the payer). */
export const ASSIGN_PAYER_NOTE =
    'We email the chosen member and they see the fee on Home and their payments page. Only they are asked to pay it, so the team no longer sees it.';

// ── Release fixes, 2 Oct 2026 ──
// CCRs 2013 reg 13 / Sch 2 and reg 28(1)(h): the cancellation position, written
// by the API from the competition's own dates (one sentence), shown word for
// word directly above the tick. Nothing when absent (an older API). Mirrored by
// the app's paying_honestly.dart.
const servedLine = (text) => {
    const line = (text ?? '').trim();
    return line || null;
};

/** The join page and request dialog: entryFeeCancellationLine, only when a fee applies. */
export const joinCancellationLine = (source) =>
    source?.entryFeeMinor != null ? servedLine(source?.entryFeeCancellationLine) : null;

/** The pay dialog and pay link page: the obligation's or pay link's cancellationLine. */
export const payCancellationLine = (source) => servedLine(source?.cancellationLine);

/** The join page names the club as the seller (CCRs reg 13), only when a fee applies. */
export const joinPayeeLine = (source) =>
    source?.entryFeeMinor != null ? payeeLine(source?.clubName, source?.clubAddress) : null;
